/**
 * Slice 41 — facts + skip persistence for Overview setup checklist.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { usePropertyRooms } from "@/hooks/usePropertyRooms";
import { useRoomCoverData } from "@/hooks/useRoomCoverPhotos";
import { roomIsSet } from "@/lib/roomCoverPhotos";
import { useInventoryItems } from "@/hooks/useInventory";
import { useInventoryRooms } from "@/hooks/useInventoryRooms";
import { useUnitItems } from "@/hooks/useUnitItems";
import { useAppliances } from "@/hooks/useAppliances";
import { usePropertyPreventativeMaintenance } from "@/hooks/usePreventativeMaintenance";
import { usePropertyGuideEntries } from "@/hooks/usePropertyGuideEntries";
import { usePropertyAssignableMembers } from "@/hooks/usePropertyAssignableMembers";
import { normalizeRoomName } from "@/lib/roomUtils";
import {
  evaluateSetupChecklist,
  itemHasLowWhen,
  parseSetupChecklistState,
  type SetupChecklistState,
  type SetupRowId,
} from "@/lib/unitSetupChecklist";

const LOCAL_KEY = (propertyId: string) => `setup-checklist:${propertyId}`;

function readLocalState(propertyId: string): SetupChecklistState {
  try {
    const raw = localStorage.getItem(LOCAL_KEY(propertyId));
    if (!raw) return {};
    return parseSetupChecklistState(JSON.parse(raw));
  } catch {
    return {};
  }
}

function writeLocalState(propertyId: string, state: SetupChecklistState) {
  try {
    localStorage.setItem(LOCAL_KEY(propertyId), JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

function mergeStates(a: SetupChecklistState, b: SetupChecklistState): SetupChecklistState {
  const skipped = { ...(a.skipped || {}), ...(b.skipped || {}) };
  return {
    skipped: Object.keys(skipped).length ? skipped : undefined,
    thresholdsReviewed: a.thresholdsReviewed || b.thresholdsReviewed || undefined,
  };
}

export function useUnitSetupChecklist(
  propertyId: string | undefined,
  property: {
    owner_id?: string | null;
    entry_method?: string | null;
    entry_code?: string | null;
    entry_location?: string | null;
    access_notes?: string | null;
    amazon_buy_list_url?: string | null;
    setup_checklist_state?: unknown;
  } | null | undefined,
) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data: roomsRaw = [] } = usePropertyRooms(propertyId);
  const { data: coverData = {} } = useRoomCoverData(propertyId);
  const { data: invItems = [] } = useInventoryItems(propertyId);
  const { data: invRooms = [] } = useInventoryRooms(propertyId);
  const { data: unitItems = [] } = useUnitItems(propertyId);
  const { data: appliances = [] } = useAppliances(propertyId);
  const { data: pmRows = [] } = usePropertyPreventativeMaintenance(propertyId);
  const { data: guideEntries = [] } = usePropertyGuideEntries(propertyId);
  const { members } = usePropertyAssignableMembers(propertyId);

  const [state, setState] = useState<SetupChecklistState>({});

  useEffect(() => {
    if (!propertyId) {
      setState({});
      return;
    }
    const db = parseSetupChecklistState(property?.setup_checklist_state);
    const local = readLocalState(propertyId);
    setState(mergeStates(db, local));
  }, [propertyId, property?.setup_checklist_state]);

  const facts = useMemo(() => {
    const rooms = roomsRaw.filter((r) => !r.archived_at);
    const roomCount = rooms.length;

    const invByPropertyRoom = new Map<string, number>();
    const unitByNorm = new Map<string, number>();
    for (const u of unitItems) {
      const n = normalizeRoomName((u as { room?: string | null }).room || "");
      if (!n) continue;
      unitByNorm.set(n, (unitByNorm.get(n) || 0) + 1);
    }

    const invRoomToProperty = new Map<string, string>();
    for (const ir of invRooms) {
      if (ir.property_room_id) invRoomToProperty.set(ir.id, ir.property_room_id);
      else {
        const match = rooms.find((r) => normalizeRoomName(r.name) === normalizeRoomName(ir.name));
        if (match) invRoomToProperty.set(ir.id, match.id);
      }
    }
    for (const it of invItems as { room_id?: string | null }[]) {
      const pid = invRoomToProperty.get(String(it.room_id || ""));
      if (!pid) continue;
      invByPropertyRoom.set(pid, (invByPropertyRoom.get(pid) || 0) + 1);
    }

    const guideRooms = new Set<string>();
    for (const g of guideEntries) {
      if (!(g.video_url || (g.photo_urls && g.photo_urls.length))) continue;
      const n = normalizeRoomName(g.room || "");
      if (n) guideRooms.add(n);
    }

    let roomsFilmedCount = 0;
    for (const room of rooms) {
      const n = normalizeRoomName(room.name);
      const items =
        (invByPropertyRoom.get(room.id) || 0) + (unitByNorm.get(n) || 0);
      if (items > 0 || guideRooms.has(n)) roomsFilmedCount += 1;
    }

    const inventoryCount = invItems.length;
    const inventoryMissingThreshold = (
      invItems as {
        quantity_type?: string | null;
        threshold_count?: number | null;
        threshold_value?: number | null;
      }[]
    ).filter((it) => !itemHasLowWhen(it)).length;

    const maintenanceReadyCount = pmRows.filter((p) => {
      if (!p.enabled) return false;
      const due = (p.next_scheduled_date || "").trim();
      const who = (p.default_assign_type || "").trim();
      return Boolean(due) && (who === "cleaner" || who === "team");
    }).length;

    const hasAccess = Boolean(
      (property?.entry_method || "").trim() ||
        (property?.entry_code || "").trim() ||
        (property?.entry_location || "").trim() ||
        (property?.access_notes || "").trim(),
    );

    const hasBuyList = Boolean((property?.amazon_buy_list_url || "").trim());
    const teamMemberCount = members.length;

    const roomsWithCoverCount = rooms.filter((r) => roomIsSet(coverData[r.id])).length;

    return {
      roomCount,
      roomsWithCoverCount,
      roomsFilmedCount,
      inventoryCount,
      inventoryMissingThreshold,
      unitItemCount: unitItems.length,
      applianceCount: appliances.length,
      maintenanceReadyCount,
      hasAccess,
      hasBuyList,
      teamMemberCount,
      state,
    };
  }, [
    roomsRaw,
    coverData,
    invItems,
    invRooms,
    unitItems,
    appliances,
    pmRows,
    guideEntries,
    members,
    property?.entry_method,
    property?.entry_code,
    property?.entry_location,
    property?.access_notes,
    property?.amazon_buy_list_url,
    state,
  ]);

  const evaluation = useMemo(() => evaluateSetupChecklist(facts), [facts]);

  const persistState = useMutation({
    mutationFn: async (next: SetupChecklistState) => {
      if (!propertyId) return;
      writeLocalState(propertyId, next);
      const { error } = await supabase
        .from("properties")
        .update({ setup_checklist_state: next } as never)
        .eq("id", propertyId);
      if (error) {
        if (error.code !== "42703" && !String(error.message).includes("setup_checklist_state")) {
          throw error;
        }
      }
    },
    onSuccess: () => {
      if (!propertyId) return;
      queryClient.invalidateQueries({ queryKey: ["property", propertyId] });
      queryClient.invalidateQueries({ queryKey: ["property-with-access", propertyId] });
      queryClient.invalidateQueries({ queryKey: ["properties", user?.id] });
    },
  });

  const skipRow = useCallback(
    (id: SetupRowId) => {
      const next: SetupChecklistState = {
        ...state,
        skipped: { ...(state.skipped || {}), [id]: true },
      };
      setState(next);
      persistState.mutate(next);
    },
    [state, persistState],
  );

  const markThresholdsReviewed = useCallback(() => {
    const next: SetupChecklistState = { ...state, thresholdsReviewed: true };
    setState(next);
    persistState.mutate(next);
  }, [state, persistState]);

  return {
    ...evaluation,
    skipRow,
    markThresholdsReviewed,
    skipping: persistState.isPending,
  };
}
